import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import type React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { SKIP_AUTH } from '../config/env';
import { setAuthToken } from '../services/api';
import { type SydonUser, sydonAuthApi } from '../services/sydonApi';
import {
  UNAUTHORIZED_EVENT,
  installUnauthorizedInterceptor,
} from '../utils/unauthorizedInterceptor';

type LogoutFn = ReturnType<typeof useLogoutFunction>;

// Matches the seed fixture org (apps/stac/src/scripts/seed.ts) — the DB's
// organization_id columns are UUID, so this must be a real UUID, not a label.
const MOCK_USER: SydonUser = {
  id: 'dev-mock-user',
  organization_id: 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11',
  role: 'admin',
  email: 'dev@sydon.ai',
};

interface RawAuthState {
  accessToken: string | null;
  user: SydonUser | null;
  loading: boolean;
  logout: LogoutFn;
}

const RawAuthContext = createContext<RawAuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const authInfo = useAuthInfo();
  const logout = useLogoutFunction();
  const [user, setUser] = useState<SydonUser | null>(null);
  const [validating, setValidating] = useState(false);
  const validatedForToken = useRef<string | null>(null);

  // Installed here (render body, not inside an effect) so the patch is active
  // before any child component's mount-time useEffect fires its first fetch —
  // idempotent via the module-level `installed` guard.
  installUnauthorizedInterceptor();

  useEffect(() => {
    if (SKIP_AUTH) return;

    let handled = false;
    const onUnauthorized = (): void => {
      if (handled) return;
      handled = true;
      logout(false).then(() => {
        window.location.href = window.location.origin;
      });
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout]);

  useEffect(() => {
    if (SKIP_AUTH) return;

    const accessToken = authInfo.accessToken ?? null;
    setAuthToken(accessToken);

    if (!accessToken) {
      validatedForToken.current = null;
      setUser(null);
      return;
    }
    if (validatedForToken.current === accessToken) return;

    validatedForToken.current = accessToken;
    setValidating(true);
    sydonAuthApi
      .validate(accessToken)
      .then(({ user: validatedUser }) => setUser(validatedUser))
      .catch(() => setUser(null))
      .finally(() => setValidating(false));
  }, [authInfo.accessToken]);

  const value: RawAuthState = SKIP_AUTH
    ? { accessToken: 'dev-mock-token', user: MOCK_USER, loading: false, logout }
    : {
        accessToken: authInfo.accessToken ?? null,
        user,
        loading: authInfo.loading || validating,
        logout,
      };

  return <RawAuthContext.Provider value={value}>{children}</RawAuthContext.Provider>;
}

// Raw, nullable session state — for the login/loading/error branches
// (RouteGuard, LoginGate, AuthCallback) that run before a session exists.
export function useAuthState(): RawAuthState {
  const ctx = useContext(RawAuthContext);
  if (!ctx) throw new Error('useAuthState must be used inside AuthProvider');
  return ctx;
}

interface AuthContextValue {
  accessToken: string;
  organizationId: string;
  user: SydonUser;
  logout: LogoutFn;
}

// Asserted, non-null session state — only valid for components RouteGuard
// has already confirmed are authenticated (i.e. everything under <App />).
export function useAuth(): AuthContextValue {
  const { accessToken, user, logout } = useAuthState();
  if (!accessToken || !user) {
    throw new Error('useAuth() called outside RouteGuard — session is not resolved yet');
  }
  return { accessToken, organizationId: user.organization_id, user, logout };
}
